import { useState, useEffect } from 'react';

interface UseScrollPositionOptions {
  throttle?: number;
  element?: HTMLElement | Window | null;
}

interface ScrollPosition {
  scrollY: number;
  scrollX: number;
  scrollDirection: 'up' | 'down' | null;
  isScrolling: boolean;
}

/**
 * Hook para monitorar posição do scroll com throttling para performance
 * Útil para Headers dinâmicos e efeitos baseados em scroll
 */
export const useScrollPosition = (options: UseScrollPositionOptions = {}): ScrollPosition => {
  const { throttle = 100, element = typeof window !== 'undefined' ? window : null } = options;

  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>({
    scrollY: 0,
    scrollX: 0,
    scrollDirection: null,
    isScrolling: false
  });

  useEffect(() => {
    if (!element) return;

    let lastScrollY = 0;
    const isScrolling = false;
    let scrollTimeout: NodeJS.Timeout;

    const updateScrollPosition = () => {
      const currentScrollY = element === window 
        ? window.scrollY 
        : (element as HTMLElement).scrollTop;
      
      const currentScrollX = element === window 
        ? window.scrollX 
        : (element as HTMLElement).scrollLeft;

      const direction = currentScrollY > lastScrollY ? 'down' : 'up';

      setScrollPosition({
        scrollY: currentScrollY,
        scrollX: currentScrollX,
        scrollDirection: currentScrollY !== lastScrollY ? direction : null,
        isScrolling: true
      });

      lastScrollY = currentScrollY;

      // Reset isScrolling after a delay
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setScrollPosition(prev => ({ ...prev, isScrolling: false }));
      }, 150);
    };

    // Throttle scroll events for performance
    let timeoutId: NodeJS.Timeout;
    const throttledScrollHandler = () => {
      if (timeoutId) return;
      
      timeoutId = setTimeout(() => {
        updateScrollPosition();
        timeoutId = null;
      }, throttle);
    };

    element.addEventListener('scroll', throttledScrollHandler, { passive: true });

    // Initial position
    updateScrollPosition();

    return () => {
      element.removeEventListener('scroll', throttledScrollHandler);
      clearTimeout(timeoutId);
      clearTimeout(scrollTimeout);
    };
  }, [element, throttle]);

  return scrollPosition;
};

/**
 * Hook especializado para Headers que mudam baseado no scroll
 */
export const useHeaderScroll = (threshold: number = 50) => {
  const { scrollY, scrollDirection } = useScrollPosition({ throttle: 50 });

  return {
    isScrolled: scrollY > threshold,
    isVisible: scrollDirection !== 'down' || scrollY <= threshold,
    scrollY,
    backgroundColor: scrollY > threshold 
      ? 'rgba(2, 6, 23, 0.95)' // fs-dark-950 with opacity
      : 'transparent'
  };
};